import { useCallback, useMemo } from "react";

import dayjs from "dayjs";

import useQueryBoard from "./useQueryBoard";
import { useAcceptedAgreements } from "../../context/Agreement";

const AUDIENCE_KEY_BY_MEMBER_TYPE: Record<
  EntranceAgreementMemberType,
  EntranceAgreementAudienceMember
> = {
  Employee: "employee",
  Visitor: "visitor",
  Guest: "guest",
  MeetingAttendee: "meetingAttendee",
};

const AUDIENCE_KEYS: EntranceAgreementAudienceMember[] = [
  "employee",
  "visitor",
  "guest",
  "meetingAttendee",
];

const isWithinValidityWindow = (target: {
  validFrom?: Date;
  validUntil?: Date;
}) => {
  const { validFrom, validUntil } = target;

  if (validFrom && dayjs().isBefore(dayjs(validFrom).startOf("day"))) {
    return false;
  }

  if (validUntil && dayjs().isAfter(dayjs(validUntil).endOf("day"))) {
    return false;
  }

  return true;
};

/**
 * A document with no audience — or with every type ticked — can be shown before
 * anyone identifies themselves. Anything narrower has to wait until we know who
 * is standing at the screen.
 */
const isForEveryone = (file: IEntranceAgreementFile) =>
  !file.audience ||
  AUDIENCE_KEYS.every((key) => file.audience?.[key]?.enabled !== false);

const isForMemberType = (
  file: IEntranceAgreementFile,
  memberType?: EntranceAgreementMemberType,
) => {
  if (!memberType) {
    return isForEveryone(file);
  }

  return (
    file.audience?.[AUDIENCE_KEY_BY_MEMBER_TYPE[memberType]]?.enabled !== false
  );
};

interface AgreementValue {
  screen: string;
  showFirst: boolean;
  /**
   * Documents that apply to everybody, so they can go up front — before a code
   * is entered, where nothing is known about the person yet.
   */
  universalAgreements: IEntranceAgreementFile[];
  /**
   * True when at least one document is limited to certain member types, which
   * is what makes resolving the type worth an extra request.
   */
  hasRestrictedAgreements: boolean;
  /**
   * Documents this person still has to go through. Without a member type only
   * the universal ones qualify.
   */
  agreementsFor: (
    memberType?: EntranceAgreementMemberType,
  ) => IEntranceAgreementFile[];
}

export const useAgreementState = (): AgreementValue => {
  const { data } = useQueryBoard();

  const { isAccepted } = useAcceptedAgreements();

  const entranceAgreement = data?.digitalReception?.entranceAgreement;

  const showFirst = entranceAgreement?.showFirst ?? false;

  /**
   * The agreement as a whole can be switched off or scheduled; either way none
   * of its documents are in play right now.
   */
  const activeFiles = useMemo(() => {
    if (!entranceAgreement || entranceAgreement.disabled) {
      return [];
    }

    if (!isWithinValidityWindow(entranceAgreement)) {
      return [];
    }

    return (entranceAgreement.files ?? []).filter(
      (file) => !file.disabled && isWithinValidityWindow(file),
    );
    /**
     * The board is refetched every few minutes and hands back a new object each
     * time, which is what re-evaluates the window — a screen can sit on the same
     * page for days, and a document that expired overnight has to disappear.
     */
  }, [entranceAgreement]);

  const universalAgreements = useMemo(
    () =>
      activeFiles.filter(isForEveryone).filter((file) => !isAccepted(file.id)),
    [activeFiles, isAccepted],
  );

  const hasRestrictedAgreements = useMemo(
    () => activeFiles.some((file) => !isForEveryone(file)),
    [activeFiles],
  );

  const agreementsFor = useCallback(
    (memberType?: EntranceAgreementMemberType) =>
      activeFiles
        .filter((file) => isForMemberType(file, memberType))
        .filter((file) => !isAccepted(file.id)),
    [activeFiles, isAccepted],
  );

  return useMemo(
    (): AgreementValue => ({
      screen: data?.digitalReception?.id || "",
      showFirst,
      universalAgreements,
      hasRestrictedAgreements,
      agreementsFor,
    }),
    [
      data,
      showFirst,
      universalAgreements,
      hasRestrictedAgreements,
      agreementsFor,
    ],
  );
};
